export const WAREHOUSE_FIX_ROUTE_TYPES = [
  "before_end",
  "end_point",
  "after_end",
  "start_point",
  "pickup_warehouse",
  "pickup_start",
  "pickup_stop",
  "pickup_end",
];

export const WAREHOUSE_RETURN_TYPES = ["goods_back", "stop_skipped", "return_order"];

export const WAREHOUSE_RETURN_STATUSES = ["pending", "delivered", "expired"];

export const STOP_ACTIONS = {
  DELIVERY: "delivery",
  PICKUP: "pickup",
  RETURN: "return",
  RETURN_TO_WAREHOUSE: "return_to_warehouse",
};

export function normalizeStopAction(value) {
  if (value == null || value === "") {
    return null;
  }

  return String(value).trim().toLowerCase().replace(/\s+/g, "_");
}

export function getStopAction(stop) {
  return normalizeStopAction(stop?.stop_action ?? stop?.stopAction);
}

export function matchesStopAction(stop, stopAction) {
  const currentAction = getStopAction(stop);
  const targetAction = normalizeStopAction(stopAction);
  const isReturnOrder = stop?.order_type === "return";

  if (targetAction === STOP_ACTIONS.DELIVERY) {
    if (isReturnOrder && currentAction !== STOP_ACTIONS.DELIVERY) return false;
    return currentAction === STOP_ACTIONS.DELIVERY || (currentAction === null && !isReturnOrder);
  }

  if (targetAction === STOP_ACTIONS.PICKUP) {
    return currentAction === STOP_ACTIONS.PICKUP || (currentAction === null && stop?.order_type === "pickup");
  }

  if (targetAction === STOP_ACTIONS.RETURN) {
    return currentAction === STOP_ACTIONS.RETURN || (currentAction === null && isReturnOrder && currentAction !== STOP_ACTIONS.PICKUP);
  }

  if (targetAction === STOP_ACTIONS.RETURN_TO_WAREHOUSE) {
    return (
      currentAction === STOP_ACTIONS.RETURN_TO_WAREHOUSE ||
      (currentAction === null && isReturnOrder && stop?.customer_id == null)
    );
  }

  return currentAction === targetAction;
}

export function getStopActionLabel(stopAction, t) {
  const normalized = normalizeStopAction(stopAction);
  if (!normalized) {
    return "-";
  }

  switch (normalized) {
    case STOP_ACTIONS.DELIVERY:
      return t("Delivery");
    case STOP_ACTIONS.PICKUP:
      return t("Pickup");
    case STOP_ACTIONS.RETURN:
      return t("Return");
    case STOP_ACTIONS.RETURN_TO_WAREHOUSE:
      return t("warehouse_return.completion_title");
    default:
      return normalized.replace(/_/g, " ");
  }
}

export function isWarehouseFixStop(pathwayItem) {
  if (!pathwayItem || pathwayItem.id != null) return false;
  if (!pathwayItem.supplier_location_id) return false;
  return WAREHOUSE_FIX_ROUTE_TYPES.includes(pathwayItem.route_type);
}

export function getWarehouseReturnPackages(pathwayItem) {
  return Array.isArray(pathwayItem?.warehouse_return_packages)
    ? pathwayItem.warehouse_return_packages
    : [];
}

export function hasWarehouseReturnPackages(pathwayItem) {
  return getWarehouseReturnPackages(pathwayItem).length > 0;
}

export function isPendingWarehouseReturn(pkg) {
  return pkg?.status === "pending";
}

export function filterWarehouseReturnsByStatus(packages, status) {
  return packages.filter((pkg) => pkg?.status === status);
}

export function countWarehouseReturnsByType(packages, { status } = {}) {
  const filtered = status ? packages.filter((p) => p?.status === status) : packages;
  return WAREHOUSE_RETURN_TYPES.reduce((acc, type) => {
    acc[type] = filtered.filter((p) => p?.type === type).length;
    return acc;
  }, {});
}

export function getFixStopRouteTypeLabel(routeType, t) {
  switch (routeType) {
    case "before_end":
      return t("Warehouse");
    case "after_end":
      return t("Warehouse");
    case "end_point":
      return t("End Location");
    default:
      return t("Warehouse");
  }
}

export function getWarehouseReturnTypeLabel(type, t) {
  switch (type) {
    case "goods_back":
      return t("warehouse_return.type.goods_back");
    case "stop_skipped":
      return t("warehouse_return.type.stop_skipped");
    case "return_order":
      return t("warehouse_return.type.return_order");
    default:
      return type || "-";
  }
}

export function getWarehouseReturnStatusLabel(status, t) {
  switch (status) {
    case "pending":
      return t("warehouse_return.status.pending");
    case "delivered":
      return t("warehouse_return.status.delivered");
    case "expired":
      return t("warehouse_return.status.expired");
    default:
      return status || "-";
  }
}

export function getFixStopsForPathwayStop(route, pathwayStop) {
  if (pathwayStop?.supplier_location_id == null) {
    return [];
  }

  return (route?.Stops || []).filter(
    (s) =>
      s.customer_id == null &&
      s.supplier_location_id === pathwayStop.supplier_location_id
  );
}

function pickLatestStop(stops) {
  if (!stops.length) {
    return null;
  }

  if (stops.length === 1) {
    return stops[0];
  }

  return [...stops].sort(
    (a, b) => new Date(b.time || 0) - new Date(a.time || 0)
  )[0];
}

export function findStopRecordByAction(route, pathwayStop, stopAction, pathwayIndex = null) {
  if (pathwayStop?.supplier_location_id == null) {
    return null;
  }

  const isPickupRouteType =
    pathwayStop.route_type !== "end_point" &&
    pathwayStop.route_type !== "before_end" &&
    pathwayStop.route_type !== "after_end";

  if (
    isPickupRouteType &&
    (stopAction === STOP_ACTIONS.RETURN || stopAction === STOP_ACTIONS.RETURN_TO_WAREHOUSE) &&
    !hasWarehouseReturnPackages(pathwayStop)
  ) {
    return null;
  }

  if (!isPickupRouteType && stopAction === STOP_ACTIONS.PICKUP) {
    return null;
  }

  const supplierId = pathwayStop.supplier_location_id;
  const pathway = route?.pathway || [];
  const stops = route?.Stops || [];

  const fixStops = stops.filter(
    (s) => s.customer_id == null && s.supplier_location_id === supplierId
  );

  if (!fixStops.length) return null;

  const actionMatches = fixStops.filter((s) => matchesStopAction(s, stopAction));
  if (!actionMatches.length) return null;

  if (actionMatches.length === 1) {
    return actionMatches[0];
  }

  const matchingPathwayStops = pathway.filter(
    (p) => p?.id == null && p?.supplier_location_id === supplierId
  );

  if (matchingPathwayStops.length > 1) {
    if (pathwayStop.route_type) {
      const explicitRouteTypeMatch = actionMatches.find(
        (s) => s.route_type === pathwayStop.route_type
      );
      if (explicitRouteTypeMatch) {
        return explicitRouteTypeMatch;
      }
    }

    let ordinal = -1;
    if (pathwayIndex != null && pathwayIndex >= 0) {
      const matchingIndices = pathway
        .map((p, i) => (p?.id == null && p?.supplier_location_id === supplierId ? i : -1))
        .filter((i) => i >= 0);
      ordinal = matchingIndices.indexOf(pathwayIndex);
    }

    if (ordinal < 0) {
      ordinal = matchingPathwayStops.indexOf(pathwayStop);
    }

    if (ordinal >= 0) {
      const sortedMatches = [...actionMatches].sort(
        (a, b) => new Date(a.time || 0) - new Date(b.time || 0) || a.id - b.id
      );
      if (sortedMatches[ordinal]) {
        return sortedMatches[ordinal];
      }
    }

    if (ordinal < 0) {
      ordinal = matchingPathwayStops.indexOf(pathwayStop);
    }

    if (ordinal >= 0) {
      const sortedMatches = [...actionMatches].sort(
        (a, b) => new Date(a.time || 0) - new Date(b.time || 0) || a.id - b.id
      );
      if (sortedMatches[ordinal]) {
        return sortedMatches[ordinal];
      }
    }

    if (ordinal < 0) {
      ordinal = matchingPathwayStops.indexOf(pathwayStop);
    }

    if (ordinal >= 0) {
      const sortedMatches = [...actionMatches].sort(
        (a, b) => new Date(a.time || 0) - new Date(b.time || 0) || a.id - b.id
      );
      if (sortedMatches[ordinal]) {
        return sortedMatches[ordinal];
      }
      return null;
    }
    return null;
  }

  return pickLatestStop(actionMatches);
}

export function findCustomerStopRecordByAction(route, customerId, stopAction) {
  if (customerId == null) return null;
  const stops = (route?.Stops || []).filter(
    (s) => s.customer_id != null && Number(s.customer_id) === Number(customerId)
  );
  const byAction = stops.filter((s) => matchesStopAction(s, stopAction));
  const match = pickLatestStop(byAction);
  if (match) {
    return match;
  }

  if (stopAction === STOP_ACTIONS.DELIVERY) {
    return stops.find((s) => s.order_type === "delivery" && s.stop_action !== STOP_ACTIONS.PICKUP) || null;
  }
  if (stopAction === STOP_ACTIONS.PICKUP) {
    return stops.find((s) => s.stop_action === STOP_ACTIONS.PICKUP || s.order_type === "pickup") || null;
  }
  if (stopAction === STOP_ACTIONS.RETURN) {
    return stops.find((s) => s.order_type === "return" && s.stop_action !== STOP_ACTIONS.PICKUP) || null;
  }

  return stops[0] || null;
}

export function resolveStopRecordByAction(
  route,
  { customerId = null, pathwayStop = null, pathwayIndex = null },
  stopAction
) {
  if (customerId != null) {
    return findCustomerStopRecordByAction(route, customerId, stopAction);
  }

  return findStopRecordByAction(route, pathwayStop, stopAction, pathwayIndex);
}

export function findFixStopRecord(route, pathwayStop, pathwayIndex) {
  return (
    findStopRecordByAction(route, pathwayStop, STOP_ACTIONS.RETURN_TO_WAREHOUSE) ||
    findWarehouseReturnStopRecordLegacy(route, pathwayStop, pathwayIndex)
  );
}

function findWarehouseReturnStopRecordLegacy(route, pathwayStop, pathwayIndex) {
  const stops = route?.Stops || [];
  const pathway = route?.pathway || [];
  const fixStops = stops.filter((s) => s.customer_id === null);

  const fixPathwayIndices = pathway
    .map((p, i) => (p?.id === null ? i : -1))
    .filter((i) => i >= 0);
  const ordinal = fixPathwayIndices.indexOf(pathwayIndex);
  if (ordinal >= 0 && fixStops[ordinal]) {
    return fixStops[ordinal];
  }

  return null;
}

export function getSupplierLocationIdFromPathwayStop(pathwayStop) {
  return pathwayStop?.supplier_location_id ?? pathwayStop?.id ?? null;
}

export function buildWarehouseReturnProofUrl(
  apiBase,
  routeId,
  pathwayStop,
  language,
  token
) {
  const supplierLocationId = getSupplierLocationIdFromPathwayStop(pathwayStop);
  if (!apiBase || !routeId || supplierLocationId == null) {
    return null;
  }

  const lang = language === "de" ? "de" : "en";
  const params = token ? `?taira=${encodeURIComponent(token)}` : "";

  return `${apiBase}routes/${routeId}/proof-of-warehouse-return/${supplierLocationId}/${lang}${params}`;
}

export function getFixStopPictures(...stopCandidates) {
  for (const candidate of stopCandidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate?.pictures) && candidate.pictures.length > 0) {
      return candidate.pictures;
    }
    if (Array.isArray(candidate?.stop_pictures) && candidate.stop_pictures.length > 0) {
      return candidate.stop_pictures;
    }
    if (typeof candidate?.pictures === "string" && candidate.pictures) {
      try {
        const parsed = JSON.parse(candidate.pictures);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch { }
      return [candidate.pictures];
    }
    if (typeof candidate?.stop_pictures === "string" && candidate.stop_pictures) {
      try {
        const parsed = JSON.parse(candidate.stop_pictures);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch { }
      return [candidate.stop_pictures];
    }
  }
  return [];
}

export function resolveOrderType(order) {
  const stopAction = normalizeStopAction(order?.stop_action ?? order?.stopAction);
  if (stopAction === STOP_ACTIONS.PICKUP) return "pickup";
  if (stopAction === STOP_ACTIONS.DELIVERY) return "delivery";
  if (stopAction === STOP_ACTIONS.RETURN || stopAction === STOP_ACTIONS.RETURN_TO_WAREHOUSE) return "return";

  if (order?.order_type) return order.order_type;
  if (order?.is_return) return "return";
  return "delivery";
}

export function resolveOrderKind(order) {
  const stopAction = normalizeStopAction(order?.stop_action ?? order?.stopAction);
  if (stopAction === STOP_ACTIONS.PICKUP) return "pickup";
  if (stopAction === STOP_ACTIONS.RETURN) return "return";
  if (stopAction === STOP_ACTIONS.DELIVERY) return "delivery";

  const orderType = resolveOrderType(order);
  if (orderType === "pickup") return "pickup";
  if (orderType === "return") return "return";
  return "delivery";
}

export function getStopRecordForOrder(route, order, pathwayStop, pathwayIndex) {
  if (!route) return pathwayStop;

  const kind = resolveOrderKind(order);
  const stopAction =
    kind === "pickup"
      ? STOP_ACTIONS.PICKUP
      : kind === "return"
        ? STOP_ACTIONS.RETURN
        : STOP_ACTIONS.DELIVERY;

  const isSupplier = pathwayStop?.id === null;
  const customerId = isSupplier
    ? null
    : order?.customer_id ?? (pathwayStop?.id !== null ? pathwayStop?.id : null);

  const context = {
    customerId,
    pathwayStop,
    pathwayIndex,
  };

  const dbStop = resolveStopRecordByAction(route, context, stopAction);
  if (dbStop) {
    return dbStop;
  }

  if (stopAction === STOP_ACTIONS.RETURN) {
    const backupStop = resolveStopRecordByAction(route, context, STOP_ACTIONS.RETURN_TO_WAREHOUSE);
    if (backupStop) return backupStop;
  }

  if (customerId != null && Array.isArray(route.Stops)) {
    const anyCustomerStop = route.Stops.find(
      (s) => s.customer_id != null && Number(s.customer_id) === Number(customerId)
    );
    if (anyCustomerStop) return anyCustomerStop;
  }

  return pathwayStop;
}

